/* Organizer Website — mandatory "How can I Bid?" section.
   The default 4-step journey (account → identity → registration → bidding)
   plus a Register CTA. Always present; the organizer may add an intro line. */
import { Gavel, IdCard, UserPlus, UserRoundCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { localized } from "@/lib/intl";

const STEPS = [
  { icon: UserPlus, key: 1 },
  { icon: IdCard, key: 2 },
  { icon: UserRoundCheck, key: 3 },
  { icon: Gavel, key: 4 },
];

export const HowToBid = ({ website, browseAnchor = "auctions", registerTo = null }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const intro = localized(website?.how_to_bid_intro, i18n.resolvedLanguage) || t("web.htbIntro");

  return (
    <section id="how-to-bid" className="scroll-mt-20 bg-white" data-testid="website-how-to-bid">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24 flex flex-col gap-12">
        <div className="flex flex-col gap-3 max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] brand-heading">{t("web.htbTitle")}</h2>
          <p className="text-sm text-[#737373] leading-relaxed">{intro}</p>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map(({ icon: Icon, key }, i) => (
            <li key={key} className="relative rounded-2xl border border-black/5 bg-[#FAFAF8] p-6 flex flex-col gap-4" data-testid={`website-htb-step-${key}`}>
              <div className="flex items-center justify-between">
                <span
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: "var(--brand-accent, #1D4D3B)" }}
                  aria-hidden="true"
                >
                  <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" />
                </span>
                <span className="text-4xl font-light tabular-nums text-black/8 leading-none select-none" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                <h3 className="text-[15px] font-semibold text-[#0A0A0A]">{t(`web.htbStep${key}Title`)}</h3>
                <p className="text-[13px] text-[#737373] leading-relaxed">{t(`web.htbStep${key}Desc`)}</p>
              </div>
            </li>
          ))}
        </ol>
        <div>
          {user ? (
            <button
              type="button"
              data-testid="website-htb-register"
              onClick={() => document.getElementById(browseAnchor)?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="inline-flex items-center h-12 px-8 rounded-full text-sm font-semibold text-white transition-opacity duration-200 hover:opacity-85"
              style={{ backgroundColor: "var(--brand-button, #0A0A0A)" }}
            >
              {t("web.htbBrowseAuctions")}
            </button>
          ) : (
            <Link
              to={registerTo || "/signup"}
              data-testid="website-htb-register"
              className="inline-flex items-center h-12 px-8 rounded-full text-sm font-semibold text-white transition-opacity duration-200 hover:opacity-85"
              style={{ backgroundColor: "var(--brand-button, #0A0A0A)" }}
            >
              {registerTo ? t("reg.cta") : t("web.registerNow")}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};
